import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import {
	Pagination,
	PaginationItem,
	PaginationLink
} from 'reactstrap';

import { FORM_TYPES } from '@techchange/components';
import { messages } from '../../../../../i18n';

import './FormDisplayEntryPagination.scss';

const NUM_ENTRIES_PER_PAGE = 3;

const propTypes = {

	/**
	 * The list of form entries.
	 */
	formEntries: PropTypes.arrayOf(PropTypes.shape({

		/**
		 * The form entry ID.
		 */
		id: PropTypes.string.isRequired,

	})).isRequired,

	/**
	 * The index of the active form entry.
	 */
	currentIndex: PropTypes.number.isRequired,

	/**
	 * The type of the form.
	 */
	type: PropTypes.oneOf([FORM_TYPES.FORM, FORM_TYPES.QUIZ]).isRequired,

	/**
	 * Callback function to change the active form entry.
	 */
	goToEntry: PropTypes.func.isRequired,

};

const FormDisplayEntryPagination = (props) => {

	const {
		formEntries,
		currentIndex,
		type,
		goToEntry,
	} = props;

	const intl = useIntl();

	// NOTE: Page 0 includes the 3 most recent entries
	//       Page n-1 includes the 3 oldest entries
	//       i.e. backwards pagination

	const [page, setPage] = useState(0);

	const totalEntries = formEntries.length;

	// When a new form entry is created, reset to the first page!
	useEffect(() => {

		setPage(0);

	}, [totalEntries]);

	// Calculate page index ranges
	const totalPages = Math.ceil(totalEntries / NUM_ENTRIES_PER_PAGE);

	const PAGE_IDX_MIN = 0;
	const PAGE_IDX_MAX = totalPages - 1;

	// Check if the entry should be displayed
	const showEntry = (entryIndex) => {

		// Calculate the actual index relative to the backwards pagination
		const index = formEntries.length - 1 - entryIndex;

		// Check if the entry is one of the 3 visible for the active page
		const isVisible =
			(index >= (page * NUM_ENTRIES_PER_PAGE) &&
			index < ((page + 1) * NUM_ENTRIES_PER_PAGE));

		if (isVisible) {
			return true;
		}

		// Check if the entry should be displayed b/c the last page is visible
		if (page === PAGE_IDX_MAX &&
			index >= totalEntries - NUM_ENTRIES_PER_PAGE) {
			return true;
		}

		return false;

	};

	const goToPrevPage = (event) => {

		event.preventDefault();

		setPage((prev) => prev - 1);

	};

	const goToNextPage = (event) => {

		event.preventDefault();

		setPage((prev) => prev + 1);

	};

	const showLeftArrowButton = page < PAGE_IDX_MAX;
	const showRightArrowButton = page > PAGE_IDX_MIN;

	let arialLabel = '';

	if (type === FORM_TYPES.FORM) {

		arialLabel = intl.formatMessage(messages.TC_COMPONENTS_RESOURCE_CONTENT_BUILDER_FORM_ENTRY_PAGINATION_BUTTON_GROUP_ARIAL_LABEL_TEXT_GO_TO_FORM_ENTRIES);

	} else if (type === FORM_TYPES.QUIZ) {

		arialLabel = intl.formatMessage(messages.TC_COMPONENTS_RESOURCE_CONTENT_BUILDER_FORM_ENTRY_PAGINATION_BUTTON_GROUP_ARIAL_LABEL_TEXT_GO_TO_QUIZ_ATTEMPTS);

	}

	return (
		<Pagination
			aria-label={arialLabel}
			className="tc-content-builder-form-entry-pagination">
			{showLeftArrowButton &&
				<PaginationItem>
					<PaginationLink
						href="#"
						previous
						onClick={goToNextPage} />
				</PaginationItem>
			}
			{formEntries.map((entry, entryIndex) => (
				<PaginationItem
					key={entry.id}
					active={currentIndex === entryIndex}
					className={showEntry(entryIndex) ? '' : 'd-none'}>
					<PaginationLink
						href="#"
						onClick={(event) => {

							event.preventDefault();

							goToEntry(entryIndex);

						}}>
						{type === FORM_TYPES.FORM &&
							<FormattedMessage
								{...messages.TC_COMPONENTS_RESOURCE_CONTENT_BUILDER_FORM_ENTRY_PAGINATION_BUTTON_TEXT_GO_TO_FORM_ENTRY}
								values={{ number: entryIndex + 1 }} />
						}
						{type === FORM_TYPES.QUIZ &&
							<FormattedMessage
								{...messages.TC_COMPONENTS_RESOURCE_CONTENT_BUILDER_FORM_ENTRY_PAGINATION_BUTTON_TEXT_GO_TO_QUIZ_ATTEMPT}
								values={{ number: entryIndex + 1 }} />
						}
					</PaginationLink>
				</PaginationItem>
			))}
			{showRightArrowButton &&
				<PaginationItem>
					<PaginationLink
						href="#"
						next
						onClick={goToPrevPage} />
				</PaginationItem>
			}
		</Pagination>
	);

};

FormDisplayEntryPagination.propTypes = propTypes;

export default FormDisplayEntryPagination;
